const locationToRegionMapping: Map<string, string> = new Map<string, string>([
  ['northAmerica', 'us'],
  ['europe', 'eu'],
  ['asia', 'ap'],
  ['australia', 'ap-au'],
]);

const locationToServiceRegionMapping: Map<string, string> = new Map<
  string,
  string
>([
  ['northAmerica', 'na'],
  ['europe', 'eu'],
  ['asia', 'ap'],
  ['australia', 'ap2'],
]);

/**
 * Maps connect location names to their corresponding connect region codes
 * @param location - The service region name (e.g., 'northAmerica', 'europe', 'asia', 'australia')
 * @returns The corresponding region code (e.g., 'us', 'eu', 'ap', 'ap-au'), or the original location if not found
 */
export const locationToRegion = (location: string): string => {
  const region = locationToRegionMapping.get(location);
  return region ?? location;
};

/**
 * Maps connect region codes to their corresponding connect location names
 * @param region - The region code (e.g., 'us', 'eu', 'ap', 'ap-au')
 * @returns The corresponding location (e.g., 'northAmerica', 'europe', 'asia', 'australia')
 */
export const regionToLocation = (region: string): string => {
  const entry = Array.from(locationToRegionMapping.entries()).find(
    ([, value]) => value === region
  );
  return entry ? entry[0] : region;
};

/**
 * Maps connect location names to their corresponding connect service region codes
 * @param location - The location name (e.g., 'northAmerica', 'europe', 'asia', 'australia')
 * @returns The corresponding region code (e.g., 'na', 'eu', 'ap', 'ap2'), or the original location if not found
 */
export const locationToServiceRegion = (location: string): string => {
  const region = locationToServiceRegionMapping.get(location);
  return region ?? location;
};

/**
 * Maps connect service region codes to their corresponding connect location names
 * @param region - The region code (e.g., 'na', 'eu', 'ap', 'ap2')
 * @returns The corresponding location (e.g., 'northAmerica', 'europe', 'asia', 'australia')
 */
export const serviceRegionToLocation = (serviceRegion: string): string => {
  const entry = Array.from(locationToServiceRegionMapping.entries()).find(
    ([, value]) => value === serviceRegion
  );
  return entry ? entry[0] : serviceRegion;
};

/**
 * Builds the Trimble Connect project TRN (`trn:connect:projects:<location>:<projectId>`)
 * used as the tenant scope key by Connect-tenanted service APIs. Defaults the
 * region to North America (`na`) when the project region is unknown.
 * @param projectId - The Trimble Connect project id
 * @param tcRegion - The project's connect service region code (e.g. 'na', 'eu'); defaults to 'na'
 * @returns The project tenant TRN
 */
export const buildProjectTenantTrn = (
  projectId: string,
  tcRegion?: string
): string =>
  `trn:connect:projects:${serviceRegionToLocation(
    tcRegion ?? 'na'
  )}:${projectId}`;
